<script setup>
import Plotly from 'plotly.js-dist'
import _ from 'lodash'
import { computed, onMounted, ref, watch } from 'vue'

const ZOOM_IN_LIMITATION = 0.05

const scaleIcon = {
  svg: '<svg xmlns="http://www.w3.org/2000/svg" fill="gray" viewBox="0 0 24 24"><title>fit-to-page-outline</title><path d="M20,2H4C2.89,2 2,2.89 2,4V20C2,21.11 2.89,22 4,22H20C21.11,22 22,21.11 22,20V4C22,2.89 21.11,2 20,2M20,20H4V4H20M13,8V10H11V8H9L12,5L15,8M16,15V13H14V11H16V9L19,12M10,13H8V15L5,12L8,9V11H10M15,16L12,19L9,16H11V14H13V16" /></svg>'
}
const props = defineProps({
  data: { required: true },
  refPlot: { required: true },
  traceIndex: { required: false, default: () => [] },
  showAnimate: { required: false, default: false },
  annotations: { required: false, default: () => [] },
  clickEvent: { required: false, default: false },
  hoverEvent: { required: false, default: false },
  showAddPoint: { type: Boolean, default: false },
  responsive: { required: false, default: true },
  hoverIds: { required: false, default: () => [], type: Array },
  xAxisRange: {
    required: false,
    default: () => [-50, 50]
  },
  yAxisRange: {
    required: false,
    default: () => [-5, 20]
  },
  highLightLine: {
    required: false,
    default: () => ({
      width: 4,
      dash: 'solid'
    })
  },
  baseLine: {
    required: false,
    default: () => ({
      width: 1,
      dash: 'dot'
    })
  },
  baseMarker: {
    required: false,
    default: () => ({
      size: 7
    })
  },
  format: {
    required: false,
    default: () => ({
      x: [],
      y: [],
      mode: 'lines+text',
      type: 'scattergl',
      name: null,
      textfont: {
        family: 'Roboto'
      }
    })
  }
})
const showAnnotations = ref(true)
const previousSelection = ref(null)
const addPointMode = ref(false)
const modeBarHeight = ref(0)
const toggleAddPointTitle = ref('Toggle Adding point by clicking')
const toggleScaleTitle = ref('Toggle scale by clicking')
const disableZoomIn = ref(false)
const scale = ref(true)
const chartXAxisRange = ref([...props.xAxisRange])
const chartYAxisRange = ref([...props.yAxisRange])
const uiRevision = ref(0)

const forceRelayout = () => {
  uiRevision.value++
  relayout()
}

const chartAnnotations = computed(() => {
  return props.annotations.filter((item) => {
    return !item || !item.hideToggle || showAnnotations.value
  })
})

const buttonsToRemove = computed(() => {
  return disableZoomIn.value ? ['zoomIn2d', 'zoom2d'] : []
})

const options = computed(() => {
  return {
    responsive: true,
    modeBarButtonsToAdd: [
      {
        name: `Toggle topo profile points`,
        icon: Plotly.Icons.tooltip_basic,
        direction: 'up',
        click: (gd) => {
          showAnnotations.value = !showAnnotations.value
          relayout()
        }
      },
      {
        name: toggleScaleTitle.value,
        icon: scaleIcon,
        direction: 'up',
        click() {
          scale.value = !scale.value
        }
      },
      ...(props.showAddPoint
        ? [
            {
              name: toggleAddPointTitle.value,
              icon: Plotly.Icons.pencil,
              direction: 'down',
              click(graph) {
                addPointMode.value = !addPointMode.value
                const path = graph.querySelector(
                  `a[data-title="${toggleAddPointTitle.value}"] > svg > path`
                )
                if (path) {
                  path.style.fill = addPointMode.value ? 'rgba(68, 68, 68, 0.7)' : null
                }
              }
            }
          ]
        : [])
    ],
    modeBarButtonsToRemove: buttonsToRemove.value
  }
})

const layout = computed(() => {
  return {
    uirevision: uiRevision.value,
    autosize: true,
    margin: {
      l: 40,
      r: 10,
      b: 30,
      t: 10,
      pad: 4
    },
    legend: {
      x: 0,
      y: 1,
      orientation: 'h'
    },
    xaxis: {
      range: chartXAxisRange.value,
      type: 'linear',
      nticks: 20
    },
    yaxis: {
      range: chartYAxisRange.value,
      type: 'linear',
      nticks: 20
    },
    annotations: chartAnnotations.value
  }
})

watch(
  () => props.annotations,
  (newValue, oldValue) => {
    if (!_.isEqual(newValue, oldValue)) {
      relayout()
    }
  }
)

watch(
  () => buttonsToRemove.value,
  (newValue, oldValue) => {
    if (!_.isEqual(newValue, oldValue)) {
      react()
    }
  }
)

watch(
  () => props.data,
  (newValue, oldValue) => {
    if (animateChangeBool(newValue, oldValue)) {
      animateChange()
    } else {
      react()
    }
  }
)

watch(
  () => props.showAddPoint,
  () => {
    addPointMode.value = false
    react()
  }
)

watch(
  () => props.xAxisRange,
  () => {
    chartXAxisRange.value = [...props.xAxisRange]
    forceRelayout()
  }
)

watch(
  () => props.yAxisRange,
  () => {
    chartYAxisRange.value = [...props.yAxisRange]
    forceRelayout()
  }
)

watch(scale, (newValue) => {
  if (newValue) {
    chartXAxisRange.value = [...props.xAxisRange]
    chartYAxisRange.value = [...props.yAxisRange]
  } else {
    let el = refPlotEl()
    
    if (el) {
      const scaleVal = el.offsetHeight / el.offsetWidth
      const yaxisValue = ((chartXAxisRange.value[1] - chartXAxisRange.value[0]) * scaleVal) / 2
      const dataYAxisCenter = (chartYAxisRange.value[1] + chartYAxisRange.value[0]) / 2
      chartYAxisRange.value = [dataYAxisCenter - yaxisValue, dataYAxisCenter + yaxisValue]
    }
  }
  forceRelayout()
})

const animateChangeBool = (newValue, oldValue) => {
  return props.showAnimate && refPlotEl()?.data?.length && newValue.length === oldValue.length
}

const animateChange = async () => {
  for (const trace of props.traceIndex) {
    await Plotly.animate(
      refPlotEl(),
      {
        data: [{ x: props.data[trace].x, y: props.data[trace].y }],
        traces: [trace],
        layout: layout.value
      },
      {
        transition: {
          duration: 200,
          easing: 'cubic-in-out'
        },
        frame: {
          duration: 200
        }
      }
    )
  }
}

const plotchart = () => {
  Plotly.newPlot(refPlotEl(), props.data, layout.value, options.value)
}

// Helper to get the plot element
const refPlotEl = () => {
  if (typeof props.refPlot === 'string') {
    return (
      document.querySelector(`[ref="${props.refPlot}"]`) || document.getElementById(props.refPlot)
    )
  }
  return props.refPlot?.$el || props.refPlot
}

const resetLine = () => {
  if (previousSelection.value) {
    previousSelection.value.line.width = props.baseLine.width
    previousSelection.value.line.dash = props.baseLine.dash
  }
}

const highLightLineOnId = (id) => {
  let item = props.data.find((item) => id === item.id)
  if (!item) {
    return
  }
  resetLine()
  item.line.width = props.highLightLine.width
  item.line.dash = props.highLightLine.dash
  previousSelection.value = item
  react()
}

const relayout = () => {
  Plotly.relayout(refPlotEl(), layout.value, options.value)
}

const plotWidth = computed(() => {
  return refPlotEl()?.clientWidth || 0
})

watch(plotWidth, () => {
  relayout()
})

const react = () => {
  return Plotly.react(refPlotEl(), props.data, layout.value, options.value)
}

const addClickEvent = () => {
  refPlotEl()?.on('plotly_click', (event) => {
    if (!props.hoverIds.length || props.hoverIds.includes(event.points[0].data.id)) {
      emit('action', event)
    }
  })
}

const addHoverEvent = () => {
  refPlotEl()?.on('plotly_hover', (event) => {
    if (!props.hoverIds.length || props.hoverIds.includes(event.points[0].data.id)) {
      emit('hover', event)
    }
  })
}

const relayoutEvent = () => {
  refPlotEl()?.on('plotly_relayout', (event) => {
    let xRange = event['xaxis.range[1]'] - event['xaxis.range[0]']
    let yRange = event['yaxis.range[1]'] - event['yaxis.range[0]']
    if (xRange < ZOOM_IN_LIMITATION || yRange < ZOOM_IN_LIMITATION) {
      disableZoomIn.value = true
    } else {
      disableZoomIn.value = false
    }
  })
}

const addArbitraryClickEvent = () => {
  refPlotEl()?.addEventListener('click', onArbitraryClick)
}

const onArbitraryClick = (event) => {
  if (!addPointMode.value) {
    return
  }

  const target = event.target
  if (target instanceof Element && target.closest('.modebar')) {
    return
  }

  const clientRect = event.target.getBoundingClientRect()
  if (event.clientY > clientRect.top + modeBarHeight.value) {
    const graph = refPlotEl()
    const { xaxis, yaxis } = graph._fullLayout
    const x = xaxis.p2d(event.clientX - clientRect.left)
    const y = yaxis.p2d(event.clientY - clientRect.top)
    emit('add-point', { x, y })
  }
}

const calculateModeBarHeight = () => {
  const graph = refPlotEl()
  const [modeBar] = graph.getElementsByClassName('modebar')
  modeBarHeight.value = modeBar ? modeBar.getBoundingClientRect().height : 0
}

onMounted(async () => {
  plotchart()
  props.clickEvent ? addClickEvent() : null
  props.hoverEvent ? addHoverEvent() : null
  relayoutEvent()
  props.showAddPoint && addArbitraryClickEvent()
  calculateModeBarHeight()
})

defineExpose({
  relayout,
  react,
  highLightLineOnId,
  resetLine,
  addClickEvent,
  addHoverEvent,
  relayoutEvent,
  addArbitraryClickEvent
})

const emit = defineEmits(['action', 'add-point', 'hover'])
</script>

<template>
  <div :class="{ 'fill-height': true, 'cursor-pencil': addPointMode }">
    <div class="fill-height" :ref="refPlot" :id="refPlot"></div>
  </div>
</template>

<style scoped>
.cursor-pencil :deep(.nsewdrag) {
  cursor: url('@/assets/pencil-line.png'), copy;
}
</style>
